import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { QuizzesApi } from "./quizzes-api";
import { BusinessesApi } from "./businesses-api";
import { UsersApi } from "./users-api";
import { CollectionsApi } from "./collections-api";
import { DebugApi } from "./debug-api";
import { ProfilesApi } from "./profiles-api";
import { ActionPromptsApi } from "./action-prompts-api";
import { TasksApi } from "./tasks-api";
import { GamesApi } from "./games-api";
import { DrawsApi } from "./draws-api";
import { LogsApi } from "./logs-api";
import { ReferalsApi } from "./referals-api";
import { ChallengesApi } from "./challenges-api";
import { NotificationsApi } from "./notifications";
import { WorkApi } from "./work-api";
import { AuthApi } from "./auth-api";
import { BoostsApi } from "./boosts-api";
import { AssetsApi } from "./assets-api";
import { AchievementsApi } from "./achievements-api";
import { RatingsApi } from "./ratings-api";
import { BonusesApi } from "./bonuses-api";
import { EventsApi } from "./events-api";
import { processApiResponseData } from "./api-response.processor";
import { getPlatform, Platform } from "../platform/platform";
import { isDebugBarEnv, setLastApiRequest } from "../debug/last-api-request";

export const resolveBackendBaseUrl = (): string => {
    const primary = process.env.REACT_APP_BACKEND_URL!;
    if (getPlatform() === Platform.Max) {
        const maxUrl = process.env.REACT_APP_BACKEND_MAX_URL;
        if (maxUrl && maxUrl.length > 0) return maxUrl;
    }
    return primary;
}

function resolveAxiosFullUrl(config: Pick<InternalAxiosRequestConfig, "baseURL" | "url">): string {
    const base = config.baseURL || "";
    const path = config.url || "";
    return base ? new URL(path, base).toString() : path;
}

function recordDebugLastRequest(
    config: Pick<InternalAxiosRequestConfig, "baseURL" | "url" | "method">,
    meta: { status?: number; error?: string } = {}
): void {
    if (!isDebugBarEnv()) return;
    setLastApiRequest({
        ts: Date.now(),
        method: String(config.method || "GET").toUpperCase(),
        url: resolveAxiosFullUrl(config),
        ...meta,
    });
}

/**
 * HTTP-клиент: транспорт, сессия (Bearer) и фасад доменных API.
 * Разбор полей ответа и доменные ручки вынесены в соседние файлы модуля api/.
 */
export class Api {
    private m_baseURL: string;
    private m_axiosInstance: AxiosInstance;
    /** Id сессии с бэка для Authorization: Bearer (после /auth/login). */
    private m_accessToken: string | null = null;

    public auth: AuthApi;
    public users: UsersApi;
    public work: WorkApi;
    public referals: ReferalsApi;
    public tasks: TasksApi;
    public draws: DrawsApi;
    public collections: CollectionsApi;
    public profiles: ProfilesApi;
    public quizzes: QuizzesApi;
    public notifications: NotificationsApi;
    public businesses: BusinessesApi;
    public actionPrompts: ActionPromptsApi;
    public debug: DebugApi;
    public logs: LogsApi;
    public games: GamesApi;
    public challenges: ChallengesApi;
    public boosts: BoostsApi;
    public assets: AssetsApi;
    public achievements: AchievementsApi;
    public ratings: RatingsApi;
    public bonuses: BonusesApi;
    public events: EventsApi;

    public static store: any;

    constructor() {
        this.m_baseURL = resolveBackendBaseUrl();

        this.m_axiosInstance = axios.create({
            baseURL: this.m_baseURL,
            withCredentials: true
        });

        this.setupInterceptors();

        this.auth = new AuthApi(this);
        this.users = new UsersApi(this);
        this.work = new WorkApi(this);
        this.referals = new ReferalsApi(this);
        this.tasks = new TasksApi(this);
        this.draws = new DrawsApi(this);
        this.collections = new CollectionsApi(this);
        this.profiles = new ProfilesApi(this);
        this.quizzes = new QuizzesApi(this);
        this.notifications = new NotificationsApi(this);
        this.businesses = new BusinessesApi(this);
        this.actionPrompts = new ActionPromptsApi(this);
        this.debug = new DebugApi(this);
        this.logs = new LogsApi(this);
        this.games = new GamesApi(this);
        this.challenges = new ChallengesApi(this);
        this.boosts = new BoostsApi(this);
        this.assets = new AssetsApi(this);
        this.achievements = new AchievementsApi(this);
        this.ratings = new RatingsApi(this);
        this.bonuses = new BonusesApi(this);
        this.events = new EventsApi(this);
    }

    public setAccessToken(token: string | null): void {
        this.m_accessToken = token;
    }

    public async get(endpoint: string) {
        const response = await this.m_axiosInstance.get(endpoint);
        return response.data;
    }

    public async post(endpoint: string, body: object) {
        const response = await this.m_axiosInstance.post(endpoint, body);
        return response.data;
    }

    private relogin(error: any): boolean {
        if (error.response?.status === 403) {
            this.m_accessToken = null;
            window.location.href = '/';
            return true;
        }

        throw error;
    }

    private setupInterceptors(): void {
        this.m_axiosInstance.interceptors.request.use((config) => {
            const nextBaseURL = resolveBackendBaseUrl();
            if (config.baseURL !== nextBaseURL) {
                this.m_baseURL = nextBaseURL;
                config.baseURL = nextBaseURL;
            }

            // nginx: map $http_x_game_platform → api_telegram (4011) | api_max (4014)
            config.headers.set(
                "X-Game-Platform",
                getPlatform() === Platform.Max ? Platform.Max : Platform.Telegram
            );

            if (this.m_accessToken) {
                config.headers.set("Authorization", `Bearer ${this.m_accessToken}`);
            }

            return config;
        });

        this.m_axiosInstance.interceptors.response.use(
            (response) => {
                processApiResponseData(response.data, Api.store);
                recordDebugLastRequest(response.config, { status: response.status });
                return response;
            },
            (error) => {
                if (error?.config) {
                    recordDebugLastRequest(error.config, {
                        status: error.response?.status,
                        error: String(error.message || "error"),
                    });
                }
                if (this.relogin(error)) {
                    return Promise.resolve({ data: {} });
                }
                throw error;
            }
        );
    }
}

let apiInstance: Api | undefined;

const getApiInstance = (): Api => {
    if (!apiInstance) {
        apiInstance = new Api();
    }
    return apiInstance;
};

/**
 * Синглтон без изменения импортов по проекту:
 * `import { api } from '../../api/api'` остаётся прежним,
 * а реальный Api создаётся при первом обращении к любому полю/методу.
 *
 * Важно: методы нужно bind к инстансу, иначе при вызове через Proxy
 * теряется `this` (в т.ч. accessToken пишется мимо синглтона).
 */
export const api: Api = new Proxy({} as Api, {
    get(_target, prop) {
        const instance = getApiInstance();
        const value = (instance as any)[prop];
        if (typeof value === "function") {
            return value.bind(instance);
        }
        return value;
    },
});
